import { useState } from 'react';
import { Box } from '@mui/material';
import {
  CheckboxV2,
  Dialog,
  RadioGroupControl,
  TextAreaV2,
  TextV2,
} from '@insights-ltd/design-library/components';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';
import { Trans, useTranslation } from 'react-i18next';
import { LearnerAndInviteeResponse } from 'api';
import { type MergeReason } from 'api/organisations/organisationApitypes';

const MAX_CUSTOM_REASON_LENGTH = 100;
const CHECKBOX_LABEL_GAP = 1.5;

const confirmationOptions = [
  {
    name: 'confirmNoUndo',
    id: 'merge-confirm-no-undo',
    label:
      'ui.event-management.merge-learner.confirmation-checkbox.profiles-from-both-learners-no-undo',
  },
  {
    name: 'confirmRemovedFromIncompleteExperiences',
    id: 'merge-confirm-removed-from-incomplete-experiences',
    label:
      'ui.event-management.merge-learner.confirmation-checkbox.removed-from-experiences-with-incomplete-evals',
  },
  {
    name: 'confirmNoOldEmailEvals',
    id: 'merge-confirm-no-old-email-evals',
    label:
      'ui.event-management.merge-learner.confirmation-checkbox.no-evals-with-old-email',
  },
] as const;

type ConfirmationName = (typeof confirmationOptions)[number]['name'];

type ConfirmationCheckboxProps = {
  id: string;
  name: string;
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
};

const ConfirmationCheckbox = ({
  id,
  name,
  label,
  checked,
  onChange,
}: ConfirmationCheckboxProps) => (
  <Box
    component="label"
    sx={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: CHECKBOX_LABEL_GAP,
      cursor: 'pointer',
    }}
  >
    <Box
      sx={(theme) => ({
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        width: theme.spacing(spacingSizeMap.M),
        height: `calc(${theme.typography.bodyBold.lineHeight} * ${theme.typography.bodyBold.fontSize})`,
      })}
    >
      <CheckboxV2
        id={id}
        name={name}
        checked={checked}
        onChange={onChange}
        required
        slotProps={{ input: { 'aria-labelledby': `${id}-label` } }}
      />
    </Box>
    <TextV2
      id={`${id}-label`}
      component="span"
      variant="bodyBold"
      color="error"
    >
      {label}
    </TextV2>
  </Box>
);

const mergeReasonOptions: { value: MergeReason; label: string }[] = [
  {
    value: 'EMAIL_CHANGE',
    label: 'ui.event-management.merge-learner.reason.one',
  },
  {
    value: 'PERSONAL_EMAIL',
    label: 'ui.event-management.merge-learner.reason.two',
  },
  {
    value: 'OTHER',
    label: 'ui.event-management.merge-learner.reason.other',
  },
];

export type MergeLearnersDialogProps = {
  target: LearnerAndInviteeResponse;
  onClose: () => void;
  onConfirm: (reason: MergeReason, customReason?: string) => void;
  isLoading: boolean;
};

const MergeLearnersDialog = ({
  target,
  onClose,
  onConfirm,
  isLoading,
}: MergeLearnersDialogProps) => {
  const { t } = useTranslation();
  const [reason, setReason] = useState<MergeReason | ''>('');
  const [customReason, setCustomReason] = useState('');
  const [checkConfirmed, setCheckConfirmed] = useState<
    Partial<Record<ConfirmationName, boolean>>
  >({});

  const trimmedCustomReason = customReason.trim();
  const isCustomReasonInvalid =
    trimmedCustomReason === '' ||
    customReason.length > MAX_CUSTOM_REASON_LENGTH;
  const isReasonInvalid =
    reason === '' || (reason === 'OTHER' && isCustomReasonInvalid);
  const allChecksConfirmed = confirmationOptions.every(
    ({ name }) => checkConfirmed[name],
  );

  const handleConfirm = () => {
    if (reason === '') {
      return;
    }
    onConfirm(reason, reason === 'OTHER' ? trimmedCustomReason : undefined);
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  return (
    <Dialog
      open
      onClose={handleClose}
      onSubmit={handleConfirm}
      isLoading={isLoading}
      title={t('ui.event-management.merge-learner.title')}
      primaryButtonText={t(
        'ui.event-management.merge-learner.combine-learners.submit',
      )}
      disablePrimaryButton={isReasonInvalid || !allChecksConfirmed}
      secondaryButtonText={t(
        'ui.event-management.merge-learner.combine-learners.cancel',
      )}
      content={
        <>
          <TextV2 component="span">
            <Trans
              i18nKey="ui.event-management.merge-learner.exists.warning"
              components={{
                bold: <TextV2 component="span" variant="bodyBold" />,
              }}
            />
          </TextV2>

          <Box sx={{ mt: spacingSizeMap.S }}>
            <TextV2 component="p" variant="bodyBold">
              {target.fullName}
            </TextV2>
            <TextV2 component="p">{target.primaryEmail}</TextV2>
          </Box>

          <Box sx={{ mt: spacingSizeMap.S }}>
            <TextV2 component="p" variant="bodyBold">
              {t('ui.event-management.merge-learner.reason.question')}
            </TextV2>
            <RadioGroupControl
              name="mergeReason"
              title={t('ui.event-management.merge-learner.reason.question')}
              options={mergeReasonOptions.map((option) => ({
                value: option.value,
                label: t(option.label),
              }))}
              value={reason}
              onChange={setReason}
            />
            {reason === 'OTHER' && (
              <TextAreaV2
                id="merge-custom-reason"
                label={t(
                  'ui.event-management.merge-learner.reason.other.field-title',
                )}
                placeholder={t(
                  'ui.event-management.merge-learner.reason.other.field-placeholder',
                )}
                value={customReason}
                autoFocus
                maxLength={MAX_CUSTOM_REASON_LENGTH}
                onChange={(event) => setCustomReason(event.target.value)}
                characterLimitLabelText={t(
                  'ui.event-management.generic.text-area.character-label',
                )}
              />
            )}
          </Box>

          <Box
            role="group"
            aria-labelledby="merge-confirmation-statement"
            sx={{
              marginTop: spacingSizeMap.S,
              display: 'flex',
              flexDirection: 'column',
              gap: spacingSizeMap.XS,
            }}
          >
            <TextV2 id="merge-confirmation-statement" color="textSecondary">
              {t('ui.event-management.merge-learner.confirmation-statement')}
            </TextV2>
            {confirmationOptions.map(({ id, name, label }) => (
              <ConfirmationCheckbox
                key={id}
                id={id}
                name={name}
                label={t(label)}
                checked={checkConfirmed[name] ?? false}
                onChange={(checked) =>
                  setCheckConfirmed((previous) => ({
                    ...previous,
                    [name]: checked,
                  }))
                }
              />
            ))}
          </Box>
        </>
      }
    />
  );
};

export default MergeLearnersDialog;
